import { Suspense, lazy, useEffect, useState } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AppStateProvider, useAppState } from './state/appState'
import { TabStrip } from './components/TabStrip'
import { AddChannelPopover } from './components/AddChannelPopover'
import { ChannelView } from './components/ChannelView'
import { TimelineView } from './components/TimelineView'
import { parseSlug } from './api/kick'

// The player route pulls in hls.js, which is by far the heaviest dependency.
// Splitting it keeps the browse view's initial payload small.
const PlayerView = lazy(() =>
  import('./components/PlayerView').then((m) => ({ default: m.PlayerView })),
)

/** `kick.com/<slug>/videos/<id>` -> { slug, id }. */
function parseVodUrl(input: string): { slug: string; id: number } | null {
  try {
    const url = new URL(input)
    if (!/(^|\.)kick\.com$/i.test(url.hostname)) return null
    const parts = url.pathname.split('/').filter(Boolean)
    if (parts.length >= 3 && parts[1] === 'videos') {
      const id = Number(parts[2])
      if (Number.isFinite(id)) return { slug: parts[0].toLowerCase(), id }
    }
  } catch {
    return null
  }
  return null
}

function Shell() {
  const navigate = useNavigate()
  const location = useLocation()
  const { ui, hasChannel, addTab, updateUi } = useAppState()
  const [adding, setAdding] = useState(false)
  const [paste, setPaste] = useState('')

  const isPlayer = location.pathname.startsWith('/v/')

  // Global shortcut: "/" focuses the paste box, "n" opens the add-channel popover.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const typing =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
      if (typing) return
      if (e.key === 'Escape' && isPlayer) {
        e.preventDefault()
        updateUi({ headerCollapsed: !ui.headerCollapsed })
      } else if (e.key === '/') {
        e.preventDefault()
        // The input lives in the collapsible header, so restore it if hidden.
        if (ui.headerCollapsed) updateUi({ headerCollapsed: false })
        document.getElementById('paste-input')?.focus()
      } else if (e.key === 'n') {
        e.preventDefault()
        if (ui.headerCollapsed) updateUi({ headerCollapsed: false })
        setAdding(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ui.headerCollapsed, updateUi, isPlayer])

  const submitPaste = (event: React.FormEvent) => {
    event.preventDefault()
    const value = paste.trim()
    if (!value) return

    const vod = parseVodUrl(value)
    if (vod) {
      navigate(`/v/${encodeURIComponent(vod.slug)}/${vod.id}`)
      setPaste('')
      return
    }

    const slug = parseSlug(value)
    if (!slug) {
      setPaste('')
      return
    }
    if (!hasChannel(slug)) addTab(slug)
    navigate(`/?tab=${encodeURIComponent(slug)}`)
    setPaste('')
  }

  return (
    <div className="app">
      {/* Collapsible so the player can claim all available space. */}
      {!(isPlayer && ui.headerCollapsed) && (
        <header className="header">
          <div className="header-bar">
            <Link to="/" className="brand">
              DGG Vods
            </Link>
            <Link
              to="/timeline"
              className="btn btn-ghost explore-link"
              aria-current={location.pathname === '/timeline' ? 'page' : undefined}
            >
              Explore endless content loop
            </Link>
            <form className="paste-form" onSubmit={submitPaste}>
              <input
                id="paste-input"
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
                placeholder="Paste a Kick VOD or channel URL…  (press / to focus)"
                spellCheck={false}
                autoComplete="off"
              />
              <button type="submit" className="btn btn-primary">
                Go
              </button>
            </form>
          </div>
          {!isPlayer && (
            <TabStrip onAddClick={() => setAdding(true)} showFavoritesOnlyToggle />
          )}
        </header>
      )}

      <main className={`app-main ${isPlayer ? 'is-player' : ''}`}>
        <Routes>
          <Route path="/" element={<ChannelView />} />
          <Route path="/timeline" element={<TimelineView />} />
          <Route
            path="/v/:slug/:id"
            element={
              <Suspense
                fallback={
                  <div className="state-box">
                    <span className="spinner" style={{ margin: '0 auto 12px' }} />
                    <p>Loading player…</p>
                  </div>
                }
              >
                <PlayerView />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {adding && (
        <AddChannelPopover
          onClose={() => setAdding(false)}
          onAdded={(slug) => {
            setAdding(false)
            if (location.pathname !== '/timeline') {
              navigate(`/?tab=${encodeURIComponent(slug)}`)
            }
          }}
        />
      )}
    </div>
  )
}

export default function App() {
  return (
    <AppStateProvider>
      <Shell />
    </AppStateProvider>
  )
}
